import { create } from "zustand";
import { isSupabaseConfigured, getSupabase } from "../supabase/client";
import { registerSuperProperties } from "../analytics";
import {
  DEFAULT_LOCALE,
  detectBrowserLocale,
  isLocale,
  type Locale,
} from "./locales";
import { DICTIONARY, interpolate, type TranslationKey } from "./dictionary";

const STORAGE_KEY = "twelve-volt:locale";

function readStored(): Locale | null {
  if (typeof window === "undefined") return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isLocale(v) ? v : null;
  } catch {
    return null;
  }
}

function writeStored(locale: Locale): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {}
}

interface I18nState {
  locale: Locale;
  profileReconciled: boolean;
  setLocale(locale: Locale): void;
  /** A locale that came from the profile: writing it back would loop the two updates. */
  applyProfileLocale(locale: Locale): void;
  markProfileReconciled(): void;
}

export const useI18n = create<I18nState>((set, get) => ({
  locale: readStored() ?? detectBrowserLocale() ?? DEFAULT_LOCALE,
  profileReconciled: false,

  setLocale: (locale) => {
    if (get().locale !== locale) set({ locale });
    writeStored(locale);
    registerSuperProperties({ locale });
    if (isSupabaseConfigured) {
      void getSupabase()
        .auth.updateUser({ data: { locale } })
        .catch(() => {
          // Not signed in, or offline — the locale is still applied locally.
        });
    }
  },

  applyProfileLocale: (locale) => {
    if (get().locale !== locale) set({ locale });
    writeStored(locale);
    registerSuperProperties({ locale });
  },

  markProfileReconciled: () => set({ profileReconciled: true }),
}));

export function useT(): (
  key: TranslationKey,
  vars?: Record<string, string | number>,
) => string {
  const locale = useI18n((s) => s.locale);
  return (key, vars) => {
    const dict = DICTIONARY[locale] ?? DICTIONARY[DEFAULT_LOCALE];
    const template = dict[key] ?? DICTIONARY[DEFAULT_LOCALE][key] ?? key;
    return interpolate(template, vars, locale);
  };
}

/** Non-hook translator — does not re-render on a locale change. */
export function t(
  key: TranslationKey,
  vars?: Record<string, string | number>,
): string {
  const locale = useI18n.getState().locale;
  const dict = DICTIONARY[locale] ?? DICTIONARY[DEFAULT_LOCALE];
  const template = dict[key] ?? DICTIONARY[DEFAULT_LOCALE][key] ?? key;
  return interpolate(template, vars, locale);
}
